
import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QrCode, Shield, Camera, CheckCircle, AlertTriangle, Eye } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";

interface PrivacyFirstScannerProps {
  onVerificationReceived?: (result: {
    status: 'verified' | 'failed';
    confidence?: number;
    timestamp: Date;
    sessionId: string;
  }) => void;
}

export const PrivacyFirstScanner = ({ onVerificationReceived }: PrivacyFirstScannerProps) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<any>(null);
  const [privacyMode, setPrivacyMode] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startScanning = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play();
      }

      setIsScanning(true);
      
      // Simulate QR code detection and verification result reception
      setTimeout(() => {
        const mockResult = {
          status: Math.random() > 0.2 ? 'verified' : 'failed' as 'verified' | 'failed',
          confidence: Math.random() > 0.3 ? Math.floor(Math.random() * 20 + 80) : undefined,
          timestamp: new Date(),
          sessionId: Math.random().toString(36).substr(2, 16),
          patronInfo: privacyMode ? null : {
            name: 'John Doe',
            age: 25
          }
        };
        
        setScanResult(mockResult);
        onVerificationReceived?.(mockResult);
        stopScanning();
      }, 3000);

    } catch (error) {
      console.error('Camera access error:', error);
      toast({
        title: "Camera Error",
        description: "Unable to access camera. Please check permissions.",
        variant: "destructive"
      });
    }
  };

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setIsScanning(false);
  };

  const resetScan = () => {
    setScanResult(null);
    stopScanning();
  };

  return (
    <div className="space-y-6">
      {/* Privacy Mode Toggle */}
      <Card className="cyber-glass border-neon-purple/30">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-neon-green" />
              <span className="text-white">Privacy-First Scanning</span>
            </div>
            <Badge 
              variant={privacyMode ? "default" : "secondary"}
              className={privacyMode ? "bg-neon-green text-black" : ""}
            >
              {privacyMode ? "Privacy ON" : "Privacy OFF"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-neon-green/10 border border-neon-green/30 rounded-lg">
            <Eye className="w-5 h-5 text-neon-green mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="text-white font-medium mb-1">Enhanced Privacy Mode Active</p>
              <p className="text-gray-300">
                Only verification status is received. No personal data or images are transmitted to venue systems.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-400">
            <div>
              <h4 className="text-white font-medium mb-2">What Venue Receives:</h4>
              <ul className="space-y-1">
                <li>✓ Verification status (verified/failed)</li>
                <li>✓ Confidence score (if available)</li>
                <li>✓ Timestamp</li>
                <li>✓ Session ID</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-2">What Venue Does NOT Receive:</h4>
              <ul className="space-y-1">
                <li>✗ ID document images</li>
                <li>✗ Biometric data</li>
                <li>✗ Personal information</li>
                <li>✗ Device camera access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scanner Interface */}
      <Card className="cyber-glass border-neon-purple/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <QrCode className="w-5 h-5" />
            QR Code Scanner
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isScanning && !scanResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center space-y-4"
            >
              <div className="w-32 h-32 mx-auto bg-black rounded-lg flex items-center justify-center border-2 border-dashed border-neon-purple/50">
                <QrCode className="w-16 h-16 text-neon-purple/50" />
              </div>
              <p className="text-gray-300">Ready to scan patron verification QR codes</p>
              <Button 
                onClick={startScanning}
                className="bg-gradient-to-r from-neon-purple to-neon-pink hover:from-neon-pink hover:to-neon-purple"
              >
                <Camera className="w-4 h-4 mr-2" />
                Start Scanning
              </Button>
            </motion.div>
          )}

          {isScanning && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-4"
            >
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-64 object-cover rounded-lg bg-black"
                />
                
                {/* Scanner overlay */}
                <div className="absolute inset-4 border-2 border-neon-purple rounded-lg pointer-events-none">
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-neon-cyan"></div>
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-neon-cyan"></div>
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-neon-cyan"></div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-neon-cyan"></div>
                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-black/50 text-white px-3 py-1 rounded text-sm">
                      Scanning for QR Code...
                    </div>
                  </div>
                </div>

                {/* Scanning animation */}
                <motion.div
                  className="absolute inset-x-4 top-4 h-1 bg-gradient-to-r from-transparent via-neon-cyan to-transparent"
                  animate={{ y: [0, 240, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
              </div>

              <div className="flex justify-center">
                <Button 
                  onClick={stopScanning}
                  variant="outline"
                  className="border-red-500 text-red-500 hover:bg-red-500/10"
                >
                  Stop Scanning
                </Button>
              </div>
            </motion.div>
          )}

          {scanResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div className="text-center space-y-4">
                {scanResult.status === 'verified' ? (
                  <>
                    <CheckCircle className="w-16 h-16 text-neon-green mx-auto" />
                    <h3 className="text-xl font-bold text-neon-green">Verification Received</h3>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-16 h-16 text-red-500 mx-auto" />
                    <h3 className="text-xl font-bold text-red-500">Verification Failed</h3>
                  </>
                )}
              </div>

              <Card className="cyber-glass border-neon-purple/20">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-400">Status</p>
                      <Badge className={
                        scanResult.status === 'verified' 
                          ? "bg-neon-green text-black" 
                          : "bg-red-500 text-white"
                      }>
                        {scanResult.status.toUpperCase()}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-gray-400">Time</p>
                      <p className="text-white">{scanResult.timestamp.toLocaleTimeString()}</p>
                    </div>
                  </div>

                  {scanResult.confidence && (
                    <div className="text-center">
                      <p className="text-gray-400">Confidence</p>
                      <p className="text-neon-cyan text-lg font-semibold">{scanResult.confidence}%</p>
                    </div>
                  )}

                  <div className="text-center text-xs text-gray-500 border-t border-neon-purple/20 pt-3">
                    Session: {scanResult.sessionId}
                  </div>
                </CardContent>
              </Card>

              <Button 
                onClick={resetScan}
                className="w-full bg-gradient-to-r from-neon-purple to-neon-pink hover:from-neon-pink hover:to-neon-purple"
              >
                Scan Next Patron
              </Button>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
